import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ArrowPathIcon, ReceiptRefundIcon } from '@heroicons/react/24/outline';
import { useCallback } from 'react';

import { resetEditing } from '../../invoice.slice';
import { RefundPaymentModal } from '../../modals/RefundPaymentModal';
import { useAppDispatch } from 'src/app/store/hooks';
import type {
  InvoicePaymentRead} from 'src/common/external/bambi-api/bambiApi';
import {
  useInvoicesRefundsCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { useModal } from 'src/common/modals/useModal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

export function RefundPaymentCell({
  payment,
}: {
  payment: InvoicePaymentRead;
}) {
  const dispatch = useAppDispatch();
  const canRefund = payment.amount_total_cents !== 0;
  const [createRefund] = useInvoicesRefundsCreateMutation();
  const { open: isRefundModalOpen, setOpen: setRefundModalOpen } = useModal();

  const handleCreateRefund = useCallback(
    async (amount_cents: number) => {
      if (!payment.id) {
        return;
      }

      try {
        await createRefund({
          invoiceRefund: {
            amount_cents,
            payment: payment.id,
            payment_intent_id: payment.is_manual
              ? '-1'
              : payment.external_payment_id,
          },
        }).unwrap();
        dispatch(
          show({
            title: 'Refund successful',
            description: 'Refunds can take between 24-48 hours to process',
            type: 'success',
          })
        );
        dispatch(resetEditing());
      } catch (e) {
        dispatch(
          show({
            title: 'Refund failed',
            description: `Failed to create refund ${formatServerError(e)}`,
            type: 'error',
          })
        );
      } finally {
        setRefundModalOpen(false);
      }
    },
    [
      createRefund,
      dispatch,
      payment.external_payment_id,
      payment.id,
      payment.is_manual,
      setRefundModalOpen,
    ]
  );

  return (
    <div className="flex flex-row justify-end print:hidden">
      {canRefund ? (
        <Button
          variant="icon"
          tooltip={
            payment.is_manual
              ? 'Unmark payment as manually paid'
              : 'Refund payment back to the payment method'
          }
          onClick={() => {
            if (payment.is_manual) {
              handleCreateRefund(payment.amount_total_cents);
            } else {
              setRefundModalOpen(true);
            }
          }}
        >
          <div
            className="flex flex-col items-center text-xs"
            style={{
              fontSize: '0.625rem',
            }}
          >
            {payment.is_manual ? (
              <>
                <ArrowPathIcon className="w-6" />
                Undo
              </>
            ) : (
              <>
                <ReceiptRefundIcon className="w-6" />
                Refund
              </>
            )}
          </div>
        </Button>
      ) : null}

      <RefundPaymentModal
        open={isRefundModalOpen}
        setOpen={setRefundModalOpen}
        payment={payment}
        onConfirm={handleCreateRefund}
      />
    </div>
  );
}
